import isEmpty from 'lodash/isEmpty';
import {
  useClearRefinements,
  useInstantSearch,
} from 'react-instantsearch-core';
import { FormattedMessage } from 'react-intl';

import { useIsMdDown } from '@/hooks/useMedia';
import { commonMessages } from '@/messages/common';
import { useRefNumberContext } from '@/providers/RefNumberProvider/RefNumberProviderContext';

import type { ListingFiltersVariant } from '../../types';

import * as S from './FiltersClearButton.styles';

interface FiltersClearButtonProps {
  variant?: ListingFiltersVariant;
}

export const FiltersClearButton = ({
  variant = 'default',
}: FiltersClearButtonProps) => {
  const { indexUiState } = useInstantSearch();
  const isMobileView = useIsMdDown();

  // Sku by default is set to ''. Therefore we need to filter it from refinement list to check if any filters or sorts are applied. If that is true Clear button should be displayed
  const refinementList = Object.values(indexUiState.refinementList || {});
  const appliedFilters = refinementList.flat().filter(Boolean);
  const hasAppliedFilterOrSort = !!(
    appliedFilters.length > 0 ||
    (!isEmpty(indexUiState.range) && indexUiState.range.price !== ':')
  );

  // On mobile filters are split in 2 lines and button is moved below the header to the white background and therefore its color needs to be adjusted
  const colorVariant =
    isMobileView && variant === 'defaultInverted' ? 'default' : variant;

  return RenderSearchViewClearButton({
    hasAppliedFilterOrSort,
    colorVariant,
  });
};

interface FiltersPlpClearButtonProps {
  colorVariant: ListingFiltersVariant;
  hasAppliedFilterOrSort: boolean;
}

const RenderSearchViewClearButton = ({
  colorVariant,
  hasAppliedFilterOrSort,
}: FiltersPlpClearButtonProps) => {
  const { canRefine, refine } = useClearRefinements();
  const { setRefNumber } = useRefNumberContext();

  const clearFilters = () => {
    refine();
    setRefNumber('');
  };

  const shouldDisplayButton = canRefine || hasAppliedFilterOrSort;

  if (!shouldDisplayButton) {
    return null;
  }

  return (
    <S.FiltersClearButton
      data-test-id='FiltersClearButton-RenderSearchViewClearButton'
      onClick={clearFilters}
      variant={colorVariant}
    >
      <FormattedMessage {...commonMessages.clear} />
    </S.FiltersClearButton>
  );
};

FiltersClearButton.displayName = 'FiltersClearButton';
